import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  PhotoIcon,
  FilmIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TagIcon,
  UserIcon,
  FolderIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useDocumentStorage } from '../../hooks/useDocumentStorage';
import { useDocumentStorageCategories } from '../../hooks/useDocumentStorageCategories';
import { useDocumentTags } from '../../hooks/useDocumentTags';
import { useCustomers } from '../../hooks/useCustomers';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { DocumentStorage } from '../../types/documentStorage';
import toast from 'react-hot-toast';

interface FileUpload {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  title?: string;
  description?: string;
  tags?: string[];
  categoryId?: string;
}

interface QuickUploadAreaProps {
  folderId?: string;
  customerId?: string;
  onUploadComplete: () => void;
  onClose: () => void;
}

export default function QuickUploadArea({
  folderId,
  customerId,
  onUploadComplete,
  onClose
}: QuickUploadAreaProps) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [defaultCategory, setDefaultCategory] = useState<string>('');
  const [defaultTags, setDefaultTags] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Kategória alapú tag javaslatok
  const categoryTagSuggestions: Record<string, string[]> = {
    'Számla': ['számla', 'pénzügy', 'kifizetés'],
    'Szerződés': ['szerződés', 'jogi', 'megállapodás'],
    'Ajánlat': ['ajánlat', 'árajánlat', 'kalkuláció'],
    'Jelentés': ['jelentés', 'riport', 'elemzés'],
    'Dokumentáció': ['dokumentáció', 'leírás', 'útmutató']
  };

  const { createDocument } = useDocumentStorage();
  const { categories } = useDocumentStorageCategories();
  const { tags: allTags } = useDocumentTags();
  const { customers } = useCustomers();
  const { user } = useAuth();
  const { t } = useLanguage();

  const generateId = () => Math.random().toString(36).substring(2, 15);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return PhotoIcon;
    if (file.type.startsWith('video/')) return FilmIcon;
    return DocumentTextIcon;
  };

  const validateFile = (file: File): string | undefined => {
    const maxFileSize = 50; // 50MB
    if (file.size > maxFileSize * 1024 * 1024) {
      return `A fájl mérete meghaladja a ${maxFileSize}MB limitet`;
    }
    return undefined;
  };

  const processFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newFiles: FileUpload[] = [];

    fileArray.forEach(file => {
      const error = validateFile(file);
      const fileUpload: FileUpload = {
        file,
        id: generateId(),
        status: error ? 'error' : 'pending',
        progress: 0,
        error,
        title: file.name.split('.').slice(0, -1).join('.') || file.name,
        description: '',
        tags: defaultTags ? defaultTags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
        categoryId: defaultCategory || categories[0]?.id
      };
      newFiles.push(fileUpload);
    });

    setSelectedFiles(prev => [...prev, ...newFiles]);
  }, [defaultCategory, defaultTags, categories]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFiles(e.dataTransfer.files);
    }
  }, [processFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFiles(e.target.files);
    }
  };

  const removeFile = (id: string) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== id));
  };

  const updateFileData = (id: string, updates: Partial<FileUpload>) => {
    setSelectedFiles(prev => prev.map(file => 
      file.id === id ? { ...file, ...updates } : file
    ));
  };

  const uploadFiles = async () => {
    const validFiles = selectedFiles.filter(f => f.status !== 'error');
    if (validFiles.length === 0) return;

    setUploading(true);

    for (const fileUpload of validFiles) {
      try {
        updateFileData(fileUpload.id, { status: 'uploading', progress: 0 });

        const documentData: any = {
          title: fileUpload.title || fileUpload.file.name,
          description: fileUpload.description || '',
          tags: fileUpload.tags || [],
          categoryId: fileUpload.categoryId || categories[0]?.id,
          customerId: customerId || 'general', // Use 'general' if no customerId provided
          folderId: folderId || undefined,
          isPublic: false,
          status: 'active' as const,
          file: fileUpload.file
        };

        // Simulate progress
        const progressInterval = setInterval(() => {
          updateFileData(fileUpload.id, { 
            progress: Math.min(fileUpload.progress + Math.random() * 20, 90) 
          });
        }, 200);

        await createDocument(documentData);

        clearInterval(progressInterval);
        updateFileData(fileUpload.id, { status: 'success', progress: 100 });

      } catch (error: any) {
        updateFileData(fileUpload.id, { 
          status: 'error', 
          error: error.message || 'Feltöltési hiba' 
        });
      }
    }

    setUploading(false);
    
    // Ha minden sikeres volt, zárjuk be és frissítsük
    const allSuccessful = selectedFiles.every(f => f.status === 'success' || f.status === 'error');
    if (allSuccessful) {
      toast.success(`${validFiles.length} fájl sikeresen feltöltve!`);
      onUploadComplete();
      setTimeout(() => {
        onClose();
      }, 1500);
    }
  };

  // Auto-select "Egyéb" category if available, otherwise first category
  useEffect(() => {
    if (categories.length > 0 && !defaultCategory) {
      const egyebCategory = categories.find(cat => 
        cat.name.toLowerCase().includes('egyéb') || 
        cat.name.toLowerCase().includes('egyeb') ||
        cat.name.toLowerCase().includes('other')
      );
      setDefaultCategory(egyebCategory?.id || categories[0].id);
    }
  }, [categories, defaultCategory]);

  const selectedCustomer = customers.find(c => c.id === customerId);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-6 transition-colors">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
            <CloudArrowUpIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Gyors fájl feltöltés
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {folderId ? 'Feltöltés ebbe a mappába' : 'Feltöltés a dokumentumtárba'}
              {selectedCustomer && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                  <UserIcon className="w-3 h-3 mr-1" />
                  {selectedCustomer.companyName || `${selectedCustomer.firstName} ${selectedCustomer.lastName}`}
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Quick Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Kategória
          </label>
          <select
            value={defaultCategory}
            onChange={(e) => setDefaultCategory(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Címkék
          </label>
          <input
            type="text"
            value={defaultTags}
            onChange={(e) => setDefaultTags(e.target.value)}
            placeholder="pl. számla, fontos"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Tag javaslatok */}
      {defaultCategory && categoryTagSuggestions[categories.find(c => c.id === defaultCategory)?.name || ''] && (
        <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
          <div className="text-xs font-medium text-purple-900 dark:text-purple-200 mb-2">💡 Javasolt címkék:</div>
          <div className="flex flex-wrap gap-2">
            {categoryTagSuggestions[categories.find(c => c.id === defaultCategory)?.name || '']?.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => {
                  const currentTags = defaultTags.split(',').map(t => t.trim()).filter(Boolean);
                  if (!currentTags.includes(tag)) {
                    setDefaultTags(currentTags.length > 0 ? `${defaultTags}, ${tag}` : tag);
                  }
                }}
                className="px-2 py-1 text-xs font-medium bg-white dark:bg-gray-800 text-purple-700 dark:text-purple-300 rounded-full border border-purple-300 dark:border-purple-600 hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
              >
                + {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Meglévő címkék */}
      {allTags.length > 0 && (
        <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
          <div className="text-xs font-medium text-blue-900 dark:text-blue-200 mb-2">🏷️ Meglévő címkék:</div>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
            {allTags.map((tag) => {
              const displayName = tag.title || tag.name;
              const currentTags = defaultTags.split(',').map(t => t.trim()).filter(Boolean);
              const isSelected = currentTags.includes(displayName);
              
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => {
                    if (isSelected) {
                      // Remove tag
                      const newTags = currentTags.filter(t => t !== displayName);
                      setDefaultTags(newTags.join(', '));
                    } else {
                      // Add tag
                      setDefaultTags(currentTags.length > 0 ? `${defaultTags}, ${displayName}` : displayName);
                    }
                  }}
                  className={`px-2 py-1 text-xs font-medium rounded-full border transition-all duration-200 ${
                    isSelected
                      ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                      : 'bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/40'
                  }`}
                  style={isSelected ? { backgroundColor: tag.color, borderColor: tag.color } : {}}
                >
                  {isSelected ? '✓ ' : '+ '}{displayName}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Drag & Drop Area - Színes */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 overflow-hidden
          ${dragActive 
            ? 'border-blue-500 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/30 dark:via-indigo-900/30 dark:to-purple-900/30 scale-105' 
            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 bg-gradient-to-br from-gray-50 to-blue-50/30 dark:from-gray-800/50 dark:to-blue-900/10'
          }
          ${uploading ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:shadow-lg'}
        `}
        onClick={() => fileInputRef.current?.click()}
      >
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInput}
          className="hidden"
          disabled={uploading}
        />
        
        <CloudArrowUpIcon className={`relative z-10 mx-auto h-10 w-10 mb-3 transition-all duration-300 ${
          dragActive ? 'text-blue-500 scale-110' : 'text-blue-400 dark:text-blue-500'
        }`} />
        
        <h3 className="relative z-10 text-base font-semibold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent mb-1">
          {dragActive ? '✨ Engedje el a fájlokat' : '📁 Húzza ide a fájlokat'}
        </h3>
        
        <p className="relative z-10 text-sm text-gray-600 dark:text-gray-300 mb-3">
          vagy kattintson a tallózáshoz
        </p>
        
        <div className="flex items-center justify-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
          <span>Max: 50MB</span>
          <span>•</span>
          <span>Minden típus</span>
        </div>
      </div>

      {/* File List */}
      {selectedFiles.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Kiválasztott fájlok ({selectedFiles.length})
            </h4>
            {!uploading && selectedFiles.some(f => f.status === 'pending') && (
              <button
                onClick={uploadFiles}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Összes feltöltése
              </button>
            )}
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {selectedFiles.map((fileUpload) => {
              const Icon = getFileIcon(fileUpload.file);
              
              return (
                <div key={fileUpload.id} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex-shrink-0">
                    <Icon className="h-8 w-8 text-gray-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={fileUpload.title || ''}
                          onChange={(e) => updateFileData(fileUpload.id, { title: e.target.value })}
                          className="text-sm font-medium text-gray-900 dark:text-gray-100 bg-transparent border-none outline-none w-full"
                          placeholder="Dokumentum címe"
                          disabled={fileUpload.status === 'uploading'}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {fileUpload.file.name} • {formatFileSize(fileUpload.file.size)}
                        </p>
                      </div>
                      
                      {fileUpload.status === 'pending' && (
                        <button
                          onClick={() => removeFile(fileUpload.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors ml-2"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                      
                      {fileUpload.status === 'success' && (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" />
                      )}
                      
                      {fileUpload.status === 'error' && (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 ml-2" />
                      )}
                    </div>
                    
                    {/* Progress bar */}
                    {fileUpload.status === 'uploading' && (
                      <div className="mt-2">
                        <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                          <div 
                            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${fileUpload.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Error message */}
                    {fileUpload.error && (
                      <p className="text-xs text-red-500 mt-1">{fileUpload.error}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
